import { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../config';
import { apiFetch } from '../utils/api';
import { Plus, Search, Trash2, Edit2, Tag, Clock, Package, TrendingUp, LayoutGrid, List, Calendar, MoreHorizontal } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { productImageSrc, retryProductImageOnce } from '../utils/productImage';

const emptyForm = { nombre: '', descripcion: '', monto: '', fecha_inicio: '', fecha_fin: '', mezcla: false, cantidad_pack: '', items: [] };

const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return new Intl.NumberFormat('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num);
};

const formatDay = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const toInputDate = (value) => (value ? String(value).slice(0, 10) : '');

const promoView = (promo) => {
    const items = (promo.items || []).filter((item) => !item.extra);
    const regular = items.reduce((sum, item) => sum + (Number(item.valor_producto) || 0) * (Number(item.cantidad) || 1), 0);
    const price = Number(promo.monto) || 0;
    const save = regular > price && regular > 0 ? Math.round((1 - price / regular) * 100) : 0;
    const units = items.reduce((sum, item) => sum + (Number(item.cantidad) || 0), 0);
    const desc = (promo.descripcion || '').trim();
    const packLabel = desc && desc.length <= 42 ? desc : (units > 1 ? `PACK ${units} UNIDADES` : '');
    const isPack = units > 1 || items.length > 1 || /pack|\dx|\sx\s/i.test(`${promo.nombre} ${desc}`);
    const photo = productImageSrc(items.find((item) => item.foto_url)?.foto_url) || '';
    const category = items.find((item) => item.categoria)?.categoria || 'Sin categoría';
    const end = promo.fecha_fin ? new Date(promo.fecha_fin) : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const soon = end && !Number.isNaN(end.getTime())
        ? (end - today) / 86400000 <= 7 && (end - today) / 86400000 >= 0
        : false;
    return { regular, price, save, units, packLabel, isPack, photo, category, soon };
};

export default function PromotionsPage() {
    const { user } = useAuth();
    const canEdit = Array.isArray(user?.permissions) && user.permissions.includes('promociones_editar');
    const canDelete = Array.isArray(user?.permissions) && user.permissions.includes('promociones_eliminar');
    const [promotions, setPromotions] = useState([]);
    const [products, setProducts] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingPromo, setEditingPromo] = useState(null);

    // Form
    const [formData, setFormData] = useState(emptyForm);
    const [query, setQuery] = useState('');
    const [estado, setEstado] = useState('todas');
    const [categoria, setCategoria] = useState('todas');
    const [tipo, setTipo] = useState('todos');
    const [vigencia, setVigencia] = useState('todas');
    const [view, setView] = useState('grid');
    const [sortBy, setSortBy] = useState('recientes');
    const [menuId, setMenuId] = useState(null);

    // Helper for adding items to promo
    const [searchTerm, setSearchTerm] = useState('');

    const fetchPromotions = async () => {
        try {
            const res = await apiFetch(`${API_URL}/api/promotions`);
            if (res.ok) setPromotions(await res.json());
        } catch (e) { console.error(e); }
    };

    const fetchProducts = async () => {
        try {
            // Fetch all products without limit
            let allProducts = [];
            let page = 1;
            let hasMore = true;
            
            while (hasMore) {
                const res = await apiFetch(`${API_URL}/api/products?limit=1000&offset=${(page - 1) * 1000}`);
                if (res.ok) {
                    const data = await res.json();
                    allProducts = [...allProducts, ...data];
                    hasMore = data.length === 1000;
                    page++;
                } else {
                    hasMore = false;
                }
            }
            
            setProducts(allProducts);
        } catch (e) { 
            console.error(e);
            // Fallback to single request
            try {
                const res = await apiFetch(`${API_URL}/api/products`);
                if (res.ok) setProducts(await res.json());
            } catch (e2) { console.error(e2); }
        }
    };

    useEffect(() => {
        // Carga inicial del catálogo.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        fetchPromotions();
        fetchProducts();
    }, []);

    const addToPromo = (product) => {
        setFormData(prev => {
            if (prev.items.find(i => i.id === product.id)) return prev;
            const fromName = String(prev.nombre || '').match(/(\d+)\s*(?:por|x)\b/i);
            const quantity = fromName ? Math.max(1, parseInt(fromName[1], 10) || 1) : 1;
            return {
                ...prev,
                items: [...prev.items, { ...product, quantity }]
            };
        });
    };

    const updateItemQty = (id, newQty) => {
        const q = Math.max(1, parseInt(newQty, 10) || 1);
        setFormData(prev => ({
            ...prev,
            items: prev.items.map(i => i.id === id ? { ...i, quantity: q } : i)
        }));
    };

    const removeItem = (id) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter(i => i.id !== id)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingPromo
                ? `${API_URL}/api/promotions/${editingPromo.id}`
                : `${API_URL}/api/promotions`;
            const method = editingPromo ? 'PUT' : 'POST';
            const res = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                alert(editingPromo ? 'Promoción actualizada con éxito' : 'Promoción creada con éxito');
                setShowModal(false);
                setEditingPromo(null);
                setFormData(emptyForm);
                fetchPromotions();
            } else {
                const err = await res.json();
                alert(err.error || 'Error al guardar promoción');
            }
        } catch (err) { console.error(err); }
    };

    const handleEdit = async (promo) => {
        try {
            const res = await apiFetch(`${API_URL}/api/promotions/${promo.id}`);
            if (!res.ok) return;
            const data = await res.json();
            setFormData({
                nombre: data.nombre || '',
                descripcion: data.descripcion || '',
                monto: String(data.monto || ''),
                fecha_inicio: toInputDate(data.fecha_inicio),
                fecha_fin: toInputDate(data.fecha_fin),
                mezcla: !!data.mezcla,
                cantidad_pack: data.cantidad_pack ? String(data.cantidad_pack) : '',
                items: (data.items || []).map(i => ({ id: i.producto_id, nombre: i.nombre, valor_producto: i.valor_producto, quantity: i.cantidad }))
            });
            setEditingPromo(promo);
            setShowModal(true);
        } catch (e) { console.error(e); }
    };

    const handleDelete = async (promo) => {
        if (!confirm(`¿Eliminar la promoción "${promo.nombre}"?`)) return;
        try {
            const res = await apiFetch(`${API_URL}/api/promotions/${promo.id}`, { method: 'DELETE' });
            if (res.ok) {
                fetchPromotions();
            } else {
                const err = await res.json();
                alert(err.error || 'Error al eliminar promoción');
            }
        } catch (e) { console.error(e); }
    };

    const filteredProducts = products.filter(p =>
        (p.nombre || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const decorated = useMemo(() => promotions.map((promo) => ({ ...promo, view: promoView(promo) })), [promotions]);
    const bestSellerId = decorated.reduce((best, promo) => (
        Number(promo.ventas_30d) > Number(best?.ventas_30d || 0) ? promo : best
    ), null)?.id;
    const categories = [...new Set(decorated.map((promo) => promo.view.category))].sort((a, b) => a.localeCompare(b, 'es'));

    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        let rows = decorated.filter((promo) => {
            const blob = [
                promo.nombre,
                promo.descripcion,
                ...(promo.items || []).flatMap((item) => [item.nombre, item.codigo_barra]),
            ].join(' ').toLowerCase();
            const matchesQuery = !q || blob.includes(q);
            const matchesCategory = categoria === 'todas' || promo.view.category === categoria;
            const matchesTipo = tipo === 'todos' || (tipo === 'pack' ? promo.view.isPack : !promo.view.isPack);
            const matchesVigencia = vigencia === 'todas'
                || (vigencia === 'por-vencer' && promo.view.soon)
                || (vigencia === 'sin-fecha' && !promo.fecha_fin)
                || (vigencia === 'con-fecha' && promo.fecha_fin);
            const matchesEstado = estado === 'todas' || estado === 'activas';
            return matchesQuery && matchesCategory && matchesTipo && matchesVigencia && matchesEstado;
        });
        rows = [...rows].sort((a, b) => {
            if (sortBy === 'ahorro') return b.view.save - a.view.save;
            if (sortBy === 'precio') return a.view.price - b.view.price;
            return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        });
        return rows;
    }, [decorated, query, categoria, tipo, vigencia, estado, sortBy]);

    const expiring = decorated.filter((promo) => promo.view.soon).length;
    const packs = decorated.filter((promo) => promo.view.isPack).length;
    const topSave = decorated.reduce((best, promo) => (promo.view.save > (best?.view.save || 0) ? promo : best), null);

    return (
        <div className="promo-screen">
            <div className="promo-head">
                <div className="promo-title">
                    <span className="promo-title-icon" aria-hidden><Tag size={20} /></span>
                    <div>
                        <h1>Promociones y Packs</h1>
                        <p>Crea y gestiona promociones para impulsar tus ventas y fidelizar clientes.</p>
                    </div>
                </div>
                {canEdit && (
                    <button type="button" className="promo-new" onClick={() => { setEditingPromo(null); setFormData(emptyForm); setShowModal(true); }}>
                        <Plus size={18} /> Nueva promoción
                    </button>
                )}
            </div>

            <section className="promo-kpis">
                <article>
                    <span className="promo-kpi-icon is-green"><Tag size={18} /></span>
                    <div>
                        <p>Promociones activas</p>
                        <strong>{promotions.length}</strong>
                        <small>En el catálogo</small>
                    </div>
                </article>
                <article>
                    <span className="promo-kpi-icon is-amber"><Clock size={18} /></span>
                    <div>
                        <p>Próximas a vencer</p>
                        <strong>{expiring}</strong>
                        <small>En los próximos 7 días</small>
                    </div>
                </article>
                <article>
                    <span className="promo-kpi-icon is-blue"><Package size={18} /></span>
                    <div>
                        <p>Packs</p>
                        <strong>{packs}</strong>
                        <small>Promociones en pack</small>
                    </div>
                </article>
                <article>
                    <span className="promo-kpi-icon is-rose"><TrendingUp size={18} /></span>
                    <div>
                        <p>Mayor ahorro</p>
                        <strong>{topSave?.view.save || 0}%</strong>
                        <small>{topSave ? topSave.nombre : 'Sin datos'}</small>
                    </div>
                </article>
            </section>

            <section className="promo-toolbar">
                <label className="promo-search">
                    <Search size={16} aria-hidden />
                    <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar promoción, producto o SKU..." />
                </label>
                <select value={estado} onChange={(e) => setEstado(e.target.value)} aria-label="Estado">
                    <option value="todas">Estado: Todas</option>
                    <option value="activas">Activas</option>
                </select>
                <select value={categoria} onChange={(e) => setCategoria(e.target.value)} aria-label="Categoría">
                    <option value="todas">Categoría: Todas</option>
                    {categories.map((name) => <option key={name} value={name}>{name}</option>)}
                </select>
                <select value={tipo} onChange={(e) => setTipo(e.target.value)} aria-label="Tipo de promoción">
                    <option value="todos">Tipo: Todos</option>
                    <option value="pack">Packs</option>
                    <option value="simple">Unitarias</option>
                </select>
                <select value={vigencia} onChange={(e) => setVigencia(e.target.value)} aria-label="Vigencia">
                    <option value="todas">Vigencia: Todas</option>
                    <option value="por-vencer">Por vencer</option>
                    <option value="con-fecha">Con fecha</option>
                    <option value="sin-fecha">Sin fecha</option>
                </select>
                <div className="promo-view">
                    <button type="button" className={view === 'grid' ? 'is-on' : ''} onClick={() => setView('grid')} aria-label="Vista de tarjetas"><LayoutGrid size={16} /></button>
                    <button type="button" className={view === 'list' ? 'is-on' : ''} onClick={() => setView('list')} aria-label="Vista de lista"><List size={16} /></button>
                </div>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} aria-label="Orden">
                    <option value="recientes">Más recientes</option>
                    <option value="ahorro">Mayor ahorro</option>
                    <option value="precio">Precio</option>
                </select>
            </section>

            <p className="promo-count">Mostrando {visible.length} promociones</p>

            <div className={view === 'grid' ? 'promo-grid' : 'promo-list'}>
                {visible.map((promo) => {
                    const { regular, price, save, packLabel, photo, category } = promo.view;
                    const range = promo.fecha_inicio || promo.fecha_fin
                        ? `${formatDay(promo.fecha_inicio) || '—'} – ${formatDay(promo.fecha_fin) || '—'}`
                        : `Desde ${formatDay(promo.created_at)}`;
                    return (
                        <article key={promo.id} className="promo-card">
                            <div className="promo-photo">
                                {photo ? <img src={photo} alt="" onError={retryProductImageOnce} /> : <span><Package size={28} /></span>}
                                {String(bestSellerId) === String(promo.id) && Number(promo.ventas_30d) > 0 && (
                                    <em className="is-hot">Más vendida</em>
                                )}
                                {save > 0 && <em className="is-save">Ahorro {save}%</em>}
                            </div>
                            <div className="promo-body">
                                <header>
                                    <h3>{promo.nombre}</h3>
                                    <span className="promo-status">Activa</span>
                                    <div className="promo-more">
                                        <button type="button" onClick={() => setMenuId(menuId === promo.id ? null : promo.id)} aria-label="Acciones">
                                            <MoreHorizontal size={16} />
                                        </button>
                                        {menuId === promo.id && (
                                            <div>
                                                {canEdit && <button type="button" onClick={() => { setMenuId(null); handleEdit(promo); }}><Edit2 size={14} /> Editar</button>}
                                                {canDelete && <button type="button" onClick={() => { setMenuId(null); handleDelete(promo); }}><Trash2 size={14} /> Eliminar</button>}
                                            </div>
                                        )}
                                    </div>
                                </header>
                                {packLabel && <p className="promo-pack">{packLabel}</p>}
                                <div className="promo-prices">
                                    <div>
                                        {regular > price && <small>Precio regular: ${formatCurrency(regular)}</small>}
                                        <small>Precio promo:</small>
                                    </div>
                                    <strong>${formatCurrency(price)}</strong>
                                </div>
                                <footer>
                                    <span><Calendar size={13} /> {range}</span>
                                    <span>{category}</span>
                                </footer>
                            </div>
                        </article>
                    );
                })}
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col md:flex-row">
                        {/* Form Side */}
                        <div className="p-6 md:w-1/2 border-r flex flex-col min-h-0 max-h-[90vh]">
                            <h2 className="modal-title shrink-0">{editingPromo ? 'Editar Promoción' : 'Crear Pack / Promoción'}</h2>
                            <form onSubmit={handleSubmit} id="promoForm" className="flex flex-col gap-4 flex-1 min-h-0">
                                <div className="overflow-y-auto flex-1 min-h-0 pr-1 space-y-4">
                                <div className="form-group full-width">
                                    <label>Nombre Promoción</label>
                                    <input required
                                        value={formData.nombre} onChange={e => setFormData({ ...formData, nombre: e.target.value })} />
                                </div>
                                <div className="form-group full-width">
                                    <label>Descripción</label>
                                    <textarea className="w-full border rounded p-2" rows="2"
                                        value={formData.descripcion} onChange={e => setFormData({ ...formData, descripcion: e.target.value })}></textarea>
                                </div>
                                <div className="form-group full-width">
                                    <label>Precio Final del Pack</label>
                                    <input required type="number" className="font-bold text-lg"
                                        value={formData.monto} onChange={e => setFormData({ ...formData, monto: e.target.value })} />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="form-group full-width">
                                        <label>Vigente desde</label>
                                        <input type="date" value={formData.fecha_inicio} onChange={e => setFormData({ ...formData, fecha_inicio: e.target.value })} />
                                    </div>
                                    <div className="form-group full-width">
                                        <label>Vigente hasta</label>
                                        <input type="date" value={formData.fecha_fin} onChange={e => setFormData({ ...formData, fecha_fin: e.target.value })} />
                                    </div>
                                </div>

                                <div className="form-group full-width">
                                    <label className="flex items-center gap-2">
                                        <input type="checkbox" checked={formData.mezcla}
                                            onChange={e => setFormData({ ...formData, mezcla: e.target.checked })} />
                                        Mezcla de sabores (cualquier combinación del grupo)
                                    </label>
                                    {formData.mezcla && (
                                        <div className="mt-2">
                                            <label>Unidades del pack (ej. 4 para «4 por $1.000»)</label>
                                            <input required type="number" min="2" max="100"
                                                value={formData.cantidad_pack}
                                                onChange={e => setFormData({ ...formData, cantidad_pack: e.target.value })} />
                                        </div>
                                    )}
                                </div>

                                <h3 className="font-bold mt-6 mb-2">{formData.mezcla ? 'Productos del grupo' : 'Productos en el Pack'}</h3>
                                <p className="text-xs text-gray-500 mb-2">
                                    {formData.mezcla
                                        ? 'Agregue todos los sabores del grupo. Al escanear cualquier combinación, el POS arma el pack solo y descuenta el stock de cada sabor.'
                                        : 'Para que el POS aplique solo al pistolear (ej. 4 galletas), ponga cantidad 4 en cada sabor o nombre la promo «4 por 1000».'}
                                </p>
                                {formData.items.length === 0 ? (
                                    <p className="text-gray-400 text-sm italic">Seleccione productos de la derecha...</p>
                                ) : (
                                    <div className="space-y-2">
                                        {formData.items.map(item => (
                                            <div key={item.id} className="flex justify-between items-center bg-gray-50 p-2 rounded">
                                                <div className="text-sm px-1">
                                                    <div className="font-medium">{item.nombre}</div>
                                                    <div className="text-xs text-gray-500">${formatCurrency(item.valor_producto)} Unit.</div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {!formData.mezcla && (
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            className="w-24 min-h-[3rem] min-w-[5rem] border-2 border-indigo-200 rounded-xl px-3 py-3 text-center text-xl font-bold text-gray-900 shadow-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-500"
                                                            value={item.quantity}
                                                            onChange={e => updateItemQty(item.id, e.target.value)}
                                                        />
                                                    )}
                                                    <button type="button" onClick={() => removeItem(item.id)} className="text-red-500"><Trash2 size={16} /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                </div>

                                <div className="mt-2 flex justify-end gap-2 shrink-0 pt-3 border-t">
                                    <button type="button" onClick={() => { setShowModal(false); setEditingPromo(null); setFormData(emptyForm); }} className="px-4 py-2 min-h-[44px] text-gray-600 hover:bg-gray-100 rounded">Cancelar</button>
                                    <button type="submit" className="btn-primary min-h-[44px]">{editingPromo ? 'Actualizar Promoción' : 'Guardar Promoción'}</button>
                                </div>
                            </form>
                        </div>

                        {/* Search Side */}
                        <div className="p-6 md:w-1/2 bg-gray-50 flex flex-col min-h-0 max-h-[90vh]">
                            <h3 className="font-bold mb-4">Agregar Productos</h3>
                            <div className="relative mb-4">
                                <Search className="absolute left-3 top-3 text-gray-400 pointer-events-none" size={18} />
                                <input type="text" placeholder="Buscar..." className="w-full border pl-14 p-2 rounded"
                                    value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                            </div>
                            <div className="space-y-2 max-h-[400px] overflow-y-auto">
                                {filteredProducts.map(p => (
                                    <button key={p.id} type="button" onClick={() => addToPromo(p)}
                                        className="w-full text-left p-3 bg-white border rounded hover:shadow-sm flex justify-between items-center group">
                                        <div>
                                            <div className="font-medium">{p.nombre}</div>
                                            <div className="text-xs text-gray-500">{p.codigo_barra}</div>
                                        </div>
                                        <Plus size={18} className="text-indigo-600 opacity-0 group-hover:opacity-100" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
